/**
 * IST (Indian Standard Time) date formatting utilities.
 * All admin panel timestamps use these functions for consistent IST display.
 *
 * Backend stores timestamps in UTC (MySQL @@session.time_zone = +00:00).
 * Timestamps arrive as strings like "2026-01-31 12:26:05" without timezone suffix.
 * parseAsUTC() ensures they are interpreted as UTC before converting to IST.
 */

const IST_OPTIONS: Intl.DateTimeFormatOptions = {
  timeZone: 'Asia/Kolkata',
};

/**
 * Parse a value as a UTC Date. If the string has no timezone indicator (Z, +, -)
 * we append 'Z' so JavaScript treats it as UTC rather than local time.
 */
function parseAsUTC(value: string | Date): Date {
  if (value instanceof Date) return value;
  const s = value.trim();
  // If it already has a timezone indicator, parse as-is
  if (/Z$/i.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) {
    return new Date(s);
  }
  // Replace space between date and time with 'T' for ISO format, then append Z
  const iso = s.replace(' ', 'T');
  return new Date(iso + 'Z');
}

/**
 * Format a date string/Date to IST datetime: "31 Jan 2026, 02:30:45 PM"
 */
export function formatIST(value: string | Date | null | undefined): string {
  if (!value) return 'N/A';
  const d = parseAsUTC(value);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-IN', {
    ...IST_OPTIONS,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

/**
 * Split IST datetime into { date, time } for compact two-line table display.
 * date: "31 Jan 2026"   time: "02:30:45 pm"
 */
export function formatISTSplit(value: string | Date | null | undefined): { date: string; time: string } {
  if (!value) return { date: 'N/A', time: '' };
  const d = parseAsUTC(value);
  if (isNaN(d.getTime())) return { date: 'N/A', time: '' };
  const datePart = d.toLocaleDateString('en-IN', {
    ...IST_OPTIONS,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const timePart = d.toLocaleTimeString('en-IN', {
    ...IST_OPTIONS,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  return { date: datePart, time: timePart };
}

/**
 * Format a date string/Date to IST date only: "31 Jan 2026"
 */
export function formatISTDate(value: string | Date | null | undefined): string {
  if (!value) return 'N/A';
  const d = parseAsUTC(value);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-IN', {
    ...IST_OPTIONS,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Format a date string/Date to short IST date for chart axes: "31 Jan"
 */
export function formatISTShort(value: string | Date | null | undefined): string {
  if (!value) return 'N/A';
  const d = parseAsUTC(value);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString('en-IN', {
    ...IST_OPTIONS,
    day: '2-digit',
    month: 'short',
  });
}
